import { Maybe } from "../utils/Maybe";

type NonEmptyArray<T> = [T, ...T[]];

export type MaybeEntityFormState<T, X> = Maybe<X & { selected: string; options: NonEmptyArray<T> }>;

const isNonEmptyArray = <T>(value: T[]): value is NonEmptyArray<T> => value.length > 0;

/**
 * Use a WeakMap to cache the transform of options array to a Map. This is a big potential performance improvement
 * because we do not need to iterate through all options when trying to determine if an option is selected.
 * A user can have thousands of VPCs and having to iterate through all of them to determine if an option
 * is selected on every render would be a performance hit. The only requirement is that the list of options must
 * be referentially equal every time this function is called.
 */
const optionsMap = new WeakMap<{}, Map<string, {}>>();

export const transformDataToMaybeEntityFormState = <T extends { value: string; disabled?: boolean }, X extends {}>(
  input: { options: T[]; defaultValue?: string | null },
  params: { currentValue: string | null; debugKey: string; extra: X }
): MaybeEntityFormState<T, X> => {
  let { defaultValue = null } = input;
  const { options } = input;
  const { currentValue, debugKey, extra } = params;

  // Filter out disabled options for selection
  const enabledOptions = options.filter((option) => !option.disabled);

  if (isNonEmptyArray(options)) {
    // If there are options, but no `defaultValue`, fallback to the first enabled option's value
    if (defaultValue === null) {
      defaultValue = enabledOptions.length > 0 ? enabledOptions[0].value : options[0].value;
    }
  } else {
    if (defaultValue !== null && process.env.NODE_ENV !== "production") {
      console.warn(
        `The callback provided to CreatePage.createRegionSelectorState returned no options for "${debugKey}", but the defaultValue was not null or undefined. This is an invalid state. Either remove the defaultValue key or set it to null.`
      );
    }
    return {
      type: "none",
    };
  }

  let map = optionsMap.get(options) as Map<string, T>;

  if (!map) {
    const entries = options.map((option) => [option.value, option] as const);
    map = new Map(entries);
    optionsMap.set(options, map);
  }

  let selected: string | null = null;

  if (currentValue !== null) {
    const option = map.get(currentValue);
    // Only select if not disabled
    if (option && !option.disabled) {
      selected = option.value;
    }
  }

  // If the `selected` value is still `null`, fall through to the first enabled option (defaultValue)
  if (selected === null) {
    const option = map.get(defaultValue);
    if (option && !option.disabled) {
      selected = option.value;
    } else if (enabledOptions.length > 0) {
      selected = enabledOptions[0].value;
    } else {
      /**
       * If the option still can't be found, then fallback to the defaultValue, which could be a value of a 'disabled' option,
       * The UI could end up in a weird state. We warn about this when not in production.
       */
      selected = defaultValue;
      if (process.env.NODE_ENV !== "production") {
        console.warn(
          `The callback provided to CreatePage.createRegionSelectorState did not return an option matching the defaultValue "${defaultValue}" for "${debugKey}".
          The defaultValue should always map to an enabled option.`
        );
      }
    }
  }

  return {
    type: "some",
    value: {
      ...extra,
      selected,
      options,
    },
  };
};
